import { z } from 'zod';
import { timestamp, uuid } from './common.js';
import { listQueryInput, paginated } from './list.js';
/**
 * Comms (§4, §7, F22/F33). `notifications`, `push_subscriptions`.
 */
export const notificationChannel = z.enum(['in_app', 'web_push', 'email']);
export const notificationSchema = z.object({
    id: uuid,
    userId: uuid,
    /** Domain event, e.g. 'job.done' | 'share.approved' | 'comment.new' | 'credit.low'. */
    type: z.string().min(1),
    /** Deterministic collapse key; a burst with the same key is ONE row (§7.2). */
    groupingKey: z.string().min(1),
    title: z.string(), // pre-rendered summary (localized to recipient at write)
    body: z.string(),
    refType: z.string().nullable(),
    refId: uuid.nullable(),
    groupCount: z.number().int().positive(),
    channels: z.array(notificationChannel),
    readAt: timestamp.nullable(),
    createdAt: timestamp,
    updatedAt: timestamp,
});
/** A browser Push subscription (F22). `410/404` on send → delete the row. */
export const pushSubscriptionSchema = z.object({
    id: uuid,
    userId: uuid,
    endpoint: z.string().url(), // UNIQUE
    p256dh: z.string().min(1),
    auth: z.string().min(1),
    userAgent: z.string().nullable(),
    createdAt: timestamp,
});
/** Client-side PushSubscription payload posted to register (F22). */
export const pushSubscribeInput = z.object({
    endpoint: z.string().url(),
    keys: z.object({
        p256dh: z.string().min(1),
        auth: z.string().min(1),
    }),
});
/** `push.subscribe` result — the persisted row's id/endpoint only (never leaks other users' rows). */
export const pushSubscribeResult = z.object({
    id: uuid,
    endpoint: z.string().url(),
});
/** `push.unsubscribe` input — the endpoint identifies which subscription to drop (F22). */
export const pushUnsubscribeInput = z.object({
    endpoint: z.string().url(),
});
export const pushUnsubscribeResult = z.object({
    removed: z.boolean(),
});
/** `notification.read` — mark one (or all when null) as read (§7.2 group reset). */
export const notificationReadInput = z.object({
    notificationId: uuid.nullable(),
});
// --- read DTOs (notification.list / notification.markRead, F33) ------------
export const notificationSortColumns = ['createdAt'];
export const notificationListQuery = listQueryInput(notificationSortColumns, {
    defaultSort: 'createdAt',
}).extend({
    /** Mirrors the bell panel's all/unread filter (F33). */
    unreadOnly: z.boolean().default(false),
});
export const notificationListResult = paginated(notificationSchema);
/**
 * `notification.markRead` — same semantics as `notificationReadInput` above
 * (mark one, or all unread when `null`, §7.2 group reset); reused under the
 * name the `wsOp` registry's newer `notification.markRead` entry uses.
 */
export const notificationMarkReadInput = notificationReadInput;
export const notificationMarkReadResult = z.object({
    markedCount: z.number().int().nonnegative(),
});
/**
 * Push on `user:{userId}` (§5.5, F13 "Revoke" / F54 admin force-logout) —
 * forces the revoked device's socket closed and its client to a logged-out
 * state. THE canonical home for this shape: `apps/server/src/ws/
 * session-revocation.ts` currently declares an identical `SessionRevokedPush`
 * interface locally (documented there as "belongs conceptually next to the
 * notifications contract... defined locally here... migration candidate") —
 * that module should import this instead of redeclaring it.
 */
export const sessionRevokedPush = z.object({
    type: z.literal('session-revoked'),
    sessionId: uuid,
});
